/**
 * The class plates — each course as a plate in the atlas.
 *
 * A plate carries the class numeral in the editorial serif, a progress ring, and
 * a spectrum strip of the subjects that class actually contains. That strip is
 * the honest version of "colour means something": Class 10's plate shows teal +
 * violet + amber because that is what Class 10 IS, and the same three hues are
 * what the course page's aurora will be mixed from when you open it.
 */

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { cn } from '../../lib/utils';
import { fetchCourses, type CourseSummary } from '../../services/learnAPI';
import { subjectMeta, glassPanel, inkMuted } from './learnShared';
import { useLearnerProgress } from './useLearnerProgress';
import { useLearnerAccess } from './useLearnerAccess';

/** "Class 10" → the numeral. A non-class track shows its initials instead. */
function plateGlyph(title: string): string {
  const m = title.match(/(\d{1,2})/);
  if (m) return m[1];
  const words = title.split(/\s+/).filter(Boolean);
  return (words[0]?.[0] ?? '?').toUpperCase() + (words[1]?.[0] ?? '').toLowerCase();
}

function Ring({ done, total, hue }: { done: number; total: number; hue: string }) {
  const r = 15;
  const c = 2 * Math.PI * r;
  const pct = total ? done / total : 0;
  return (
    <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90" aria-hidden="true">
      <circle cx="18" cy="18" r={r} fill="none" strokeWidth="3" className="stroke-[#191831]/12 dark:stroke-white/15" />
      {pct > 0 && (
        <circle
          cx="18" cy="18" r={r} fill="none" strokeWidth="3" strokeLinecap="round"
          stroke={hue} strokeDasharray={`${c * pct} ${c}`}
        />
      )}
    </svg>
  );
}

export function CourseShelf() {
  const [courses, setCourses] = useState<CourseSummary[] | null>(null);
  const { items, byToken } = useLearnerProgress();
  const access = useLearnerAccess();

  useEffect(() => {
    fetchCourses().then(setCourses).catch(() => setCourses([]));
  }, []);

  // A plate for a class holding nothing this learner was given is a door to an
  // empty room.
  const shown = (courses ?? []).filter((c) => access.courseVisible(c.id));
  if (!courses || shown.length === 0) return null;

  // The progress rows span the whole catalogue (not_started included), so both a
  // course's subject mix and its completion come from them — one source, no
  // extra fetch, and the plate can never disagree with the course page.
  const subjectsIn = (courseId: string) => {
    const seen: string[] = [];
    for (const row of items) {
      if (row.courseId === courseId && row.chapter && !seen.includes(row.chapter)) seen.push(row.chapter);
    }
    return seen;
  };
  const doneIn = (courseId: string) =>
    [...byToken.values()].filter((p) => p.courseId === courseId && p.status === 'completed').length;

  return (
    <section className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Your atlas</p>
          <h2 className="mt-1 font-serif text-[30px] leading-[1.14] tracking-tight">Your classes</h2>
        </div>
        <span className={cn('text-[13px]', inkMuted)}>Each plate glows the subjects it holds</span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {shown.map((course) => {
          const subjects = subjectsIn(course.id);
          const done = doneIn(course.id);
          const glyph = plateGlyph(course.title);
          const lead = subjectMeta(subjects[0] ?? null);
          return (
            <Link
              key={course.id}
              to="/learn/course/$courseId"
              params={{ courseId: course.id }}
              className={cn(
                glassPanel,
                'group relative flex aspect-[4/5] flex-col justify-between overflow-hidden p-4 transition-all duration-300 hover:-translate-y-1.5 hover:bg-white/[0.92] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] dark:hover:bg-[#181A2A]/[0.85]',
              )}
            >
              {/* The plate's own light, from its own subjects. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-70 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  backgroundImage: (subjects.length ? subjects : [null])
                    .slice(0, 3)
                    .map((s, i) => `radial-gradient(75% 60% at ${18 + i * 32}% ${88 - i * 14}%, ${subjectMeta(s).aurora} 0%, transparent 70%)`)
                    .join(', '),
                }}
              />

              <div className="relative flex items-start justify-between">
                <p className={cn('text-[10px] font-bold uppercase tracking-[0.14em]', inkMuted)}>
                  {course.gradeLevel ? 'Class' : 'Course'}
                </p>
                <Ring done={done} total={course.lessonCount} hue={lead.color} />
              </div>

              <div className="relative">
                <p
                  className="font-serif tracking-tight"
                  style={{ fontSize: glyph.length > 1 ? '3.4rem' : '4.4rem', lineHeight: 0.86 }}
                >
                  {glyph}
                </p>
                <p className="mt-2 truncate text-[13px] font-semibold">{course.title}</p>
                <p className={cn('mt-0.5 text-[11.5px] font-medium tabular-nums', inkMuted)}>
                  {done > 0 ? `${done} of ${course.lessonCount} done` : `${course.lessonCount} lessons`}
                </p>

                {/* The spectrum strip: this class's actual subjects. */}
                {subjects.length > 0 && (
                  <div className="mt-2.5 flex gap-1" aria-hidden="true">
                    {subjects.slice(0, 4).map((s) => (
                      <span key={s} className="h-[3px] flex-1 rounded-full" style={{ background: subjectMeta(s).color }} />
                    ))}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
